/**
 * Saturday and Sunday Working Status Configuration
 * 
 * This configuration file manages working day policies for the organization.
 * It determines whether Saturday and Sunday are considered working days,
 * and provides utilities for date calculations that respect these policies.
 * Also integrates with public holidays from API.
 */

import { isPublicHoliday, getHolidayDetails, loadHolidaysFromAPI } from './holidays';

// Working Days Configuration (will be updated from API)
export const WORKING_DAYS_CONFIG = {
  // Set to true if Saturday is a working day, false otherwise (will be updated from API)
  SATURDAY_WORKING: false,
  
  // Set to true if Sunday is a working day, false otherwise (will be updated from API)
  SUNDAY_WORKING: false,
  
  // Organization's working week (1 = Monday, 7 = Sunday)
  WORKING_DAYS: [1, 2, 3, 4, 5], // Monday to Friday by default
  
  // Set to true to skip public holidays when calculating working days
  SKIP_PUBLIC_HOLIDAYS: true,
  
  // API loaded flag
  API_LOADED: false
};

/**
 * Initialize working days configuration from API
 * Should be called when the app starts
 * @param {string} countryCode - Country code (default: 'IN')
 * @returns {Promise<void>}
 */
export const initializeWorkingDaysConfig = async (countryCode = 'IN') => {
  if (WORKING_DAYS_CONFIG.API_LOADED) {
    console.log('Working days config already loaded from API');
    return;
  }
  
  try {
    console.log('Initializing working days configuration from API...');
    const apiData = await loadHolidaysFromAPI(countryCode);
    
    // Update Saturday/Sunday working status from API
    WORKING_DAYS_CONFIG.SATURDAY_WORKING = apiData.saturdayWorking;
    WORKING_DAYS_CONFIG.SUNDAY_WORKING = apiData.sundayWorking;
    
    // Update working days array
    WORKING_DAYS_CONFIG.WORKING_DAYS = [1, 2, 3, 4, 5]; // Mon-Fri always included
    if (apiData.saturdayWorking) {
      WORKING_DAYS_CONFIG.WORKING_DAYS.push(6); // Add Saturday
    }
    if (apiData.sundayWorking) {
      WORKING_DAYS_CONFIG.WORKING_DAYS.push(0); // Add Sunday
    }
    
    WORKING_DAYS_CONFIG.API_LOADED = true;
    console.log('Working days config loaded:', {
      saturdayWorking: WORKING_DAYS_CONFIG.SATURDAY_WORKING,
      sundayWorking: WORKING_DAYS_CONFIG.SUNDAY_WORKING,
      holidaysCount: apiData.holidays.length
    });
  } catch (error) {
    console.error('Error initializing working days config, using defaults:', error);
    // Keep default values if API fails
    WORKING_DAYS_CONFIG.SATURDAY_WORKING = false;
    WORKING_DAYS_CONFIG.SUNDAY_WORKING = false;
    WORKING_DAYS_CONFIG.WORKING_DAYS = [1, 2, 3, 4, 5];
  }
};

/**
 * Check if a given date is a working day based on the configuration
 * Considers both weekends and public holidays
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a working day, false otherwise
 */
export const isWorkingDay = (date) => {
  const day = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Check if it's a public holiday first
  if (WORKING_DAYS_CONFIG.SKIP_PUBLIC_HOLIDAYS && isPublicHoliday(date)) {
    return false;
  }
  
  // Check if it's Saturday (6) or Sunday (0)
  if (day === 0) {
    return WORKING_DAYS_CONFIG.SUNDAY_WORKING;
  }
  if (day === 6) {
    return WORKING_DAYS_CONFIG.SATURDAY_WORKING;
  }
  
  // Monday to Friday are working days (unless they're holidays)
  return true;
};

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is on a weekend
 */
export const isWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6; // Sunday or Saturday
};

/**
 * Get the next working day from a given date
 * @param {Date} date - The starting date
 * @returns {Date} - The next working day
 */
export const getNextWorkingDay = (date) => {
  const nextDay = new Date(date);
  nextDay.setDate(nextDay.getDate() + 1);
  
  // Keep incrementing until we find a working day
  while (!isWorkingDay(nextDay)) {
    nextDay.setDate(nextDay.getDate() + 1);
  }
  
  return nextDay;
};

/**
 * Calculate due date by adding working days (excluding non-working days)
 * @param {Date} startDate - The start date
 * @param {number} workingDays - Number of working days to add
 * @returns {Date} - The calculated due date
 */
export const calculateDueDateWithWorkingDays = (startDate, workingDays) => {
  // Create a new date object to avoid mutating the input
  let currentDate = new Date(startDate.getTime());
  let daysAdded = 0;
  
  // Ensure we start from a working day
  if (!isWorkingDay(currentDate)) {
    // If start date is not a working day, move to next working day
    currentDate = getNextWorkingDay(currentDate);
  }
  
  // Now count working days
  // For a 1-day task starting Monday, due date is Monday (same day)
  // For a 2-day task starting Monday, due date is Tuesday
  // etc.
  daysAdded = 1; // Start date counts as day 1
  
  // Add remaining working days (workingDays - 1 because start date is day 1)
  while (daysAdded < workingDays) {
    currentDate.setDate(currentDate.getDate() + 1);
    // Skip weekends
    while (!isWorkingDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    daysAdded++;
  }
  
  return currentDate;
};

/**
 * Check if a date string should be disabled in date pickers
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} - True if the date should be disabled
 */
export const shouldDisableDate = (dateString) => {
  const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
  return !isWorkingDay(date);
};

/**
 * Get disabled days for MUI DatePicker
 * @returns {function} - Function that returns true for disabled days
 */
export const getDisabledDays = () => {
  return (date) => {
    return !isWorkingDay(date);
  };
};

/**
 * Format working days configuration as human-readable text
 * @returns {string} - Description of working days
 */
export const getWorkingDaysDescription = () => {
  const days = [];
  
  if (WORKING_DAYS_CONFIG.WORKING_DAYS.includes(1)) days.push('Monday');
  if (WORKING_DAYS_CONFIG.WORKING_DAYS.includes(2)) days.push('Tuesday');
  if (WORKING_DAYS_CONFIG.WORKING_DAYS.includes(3)) days.push('Wednesday');
  if (WORKING_DAYS_CONFIG.WORKING_DAYS.includes(4)) days.push('Thursday');
  if (WORKING_DAYS_CONFIG.WORKING_DAYS.includes(5)) days.push('Friday');
  if (WORKING_DAYS_CONFIG.SATURDAY_WORKING) days.push('Saturday');
  if (WORKING_DAYS_CONFIG.SUNDAY_WORKING) days.push('Sunday');
  
  if (days.length === 0) return 'No working days configured';
  if (days.length === 7) return 'All days (Monday to Sunday)';
  
  return days.join(', ');
};

/**
 * Get weekend status message
 * @returns {string} - Message about weekend working status
 */
export const getWeekendStatusMessage = () => {
  let message = '';
  
  if (WORKING_DAYS_CONFIG.SATURDAY_WORKING && WORKING_DAYS_CONFIG.SUNDAY_WORKING) {
    message = 'Weekends (Saturday & Sunday) are working days';
  } else if (WORKING_DAYS_CONFIG.SATURDAY_WORKING) {
    message = 'Saturday is a working day, Sunday is not';
  } else if (WORKING_DAYS_CONFIG.SUNDAY_WORKING) {
    message = 'Sunday is a working day, Saturday is not';
  } else {
    message = 'Weekends (Saturday & Sunday) are non-working days';
  }
  
  // Add holiday information if enabled
  if (WORKING_DAYS_CONFIG.SKIP_PUBLIC_HOLIDAYS) {
    message += '. Public holidays are also non-working days';
  }
  
  return message;
};
